import styled, { css } from 'styled-components';

import { SPACING } from '~/common/constants/spacing';
import { RADIUS } from '~/common/constants/border-radius';

import { TTextInputSize } from './types';
import {
  getParagraphFontSize,
  getParagraphLetterSpacing,
  getParagraphLineHeight,
} from '../Typography/Paragraph/styles';

interface IInputWrapperProps {
  $hasError: boolean;
  $size: TTextInputSize;
  $width?: string;
  $isDisabled: boolean;
  $hasPrefixOrSuffix: boolean;
  $isPassword: boolean;
  $withoutBorder: boolean;
  $withOnlyBorderBottom: boolean;
  $inputAs: 'input' | 'textarea';
  $textAreaHeight: string | null;
}

interface ICounterWrapperProps {
  $size: TTextInputSize;
}

export const Container = styled.label`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._4};

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
`;

export const LoaderWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;

  width: min-content;
  margin-left: auto;
  > span {
    height: 20px !important;
    width: 20px !important;
  }
`;

export const getTextInputHeight: Record<TTextInputSize, string> = {
  'x-small': '32px',
  small: '36px',
  medium: '40px',
  large: '48px',
};

const getTextInputPadding: Record<TTextInputSize, string> = {
  'x-small': `${SPACING._6}`,
  small: `${SPACING._8}`,
  medium: `${SPACING._10}`,
  large: `${SPACING._10}`,
};

const getTextInputGap: Record<TTextInputSize, string> = {
  'x-small': `${SPACING._6}`,
  small: `${SPACING._8}`,
  medium: `${SPACING._8}`,
  large: `${SPACING._8}`,
};

export const CopySuffixWrapper = styled.div<{
  $size: TTextInputSize;
  $focus: boolean;
}>`
  cursor: pointer;
  border-left: ${({ theme }) => `1px solid ${theme.neutral[700]}`};
  padding: ${({ $size }) => getTextInputPadding[$size]};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ease-in 0.2s;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.neutral[0]};
    `}
`;

export const PrefixOrSuffixWrapper = styled.div<{
  $size: TTextInputSize;
  $focus: boolean;
  $borderPosition: 'left' | 'right';
}>`
  ${({ $borderPosition }) =>
    $borderPosition === 'right'
      ? css`
          border-right: ${({ theme }) => `1px solid ${theme.neutral[700]}`};
        `
      : css`
          border-left: ${({ theme }) => `1px solid ${theme.neutral[700]}`};
        `}

  padding: ${({ $size }) => getTextInputPadding[$size]};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ease-in 0.2s;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.neutral[0]};
    `}
`;

export const InputWrapper = styled.div<IInputWrapperProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: ${({ $width }) => $width};
  border-radius: ${RADIUS._4};
  border: ${({ theme, $withoutBorder, $withOnlyBorderBottom }) =>
    $withoutBorder || $withOnlyBorderBottom
      ? 'unset'
      : `1px solid ${theme.neutral[800]}`};

  ${({ $withOnlyBorderBottom, theme }) =>
    $withOnlyBorderBottom &&
    css`
      border-bottom: 1px solid ${theme.neutral[700]};
    `}

  background-color: ${({ theme }) => theme.neutral[950]};
  padding: ${({ $size, $hasPrefixOrSuffix }) =>
    $hasPrefixOrSuffix ? 0 : getTextInputPadding[$size]};
  padding-right: ${({ $isPassword }) => $isPassword && '0 !important'};
  gap: ${({ $size }) => getTextInputGap[$size]};
  transition: all ease-in 0.2s;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 0s 600000s, color 0s 600000s !important;
    appearance: unset !important;
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.neutral[950]} inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.neutral[0]} !important;
    caret-color: ${({ theme }) => theme.neutral[0]} !important;
    color: ${({ theme }) => theme.neutral[0]} !important;
    background-color: ${({ theme }) => theme.neutral[950]} !important;
    border: none !important;
    text-decoration: none !important;
    outline: none !important;
  }

  ${({ $hasPrefixOrSuffix, $size }) =>
    $hasPrefixOrSuffix &&
    css`
      input {
        padding: ${getTextInputPadding[$size]};
      }

      #input-icon-id {
        padding: ${getTextInputPadding[$size]};
      }
    `}

  ${({ $inputAs, $size }) =>
    $inputAs === 'input' &&
    css`
      height: ${getTextInputHeight[$size]};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.neutral[900]};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.neutral[0]};
  }

  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: ${({ theme }) => theme.red[600]} !important;
    `};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      border-color: ${({ theme }) => theme.neutral[900]};
      background-color: ${({ theme }) => theme.neutral[900]};
      cursor: not-allowed;
    `};

  ${({ $textAreaHeight, $width }) =>
    $textAreaHeight &&
    css`
      min-height: ${$textAreaHeight};
      max-width: ${$width};
    `}
`;

export const CustomInput = styled.input<{
  centralize: boolean;
  textAreaHeight: string;
}>`
  background: none;
  background-color: transparent !important;
  width: 100%;
  height: 100%;
  overflow: hidden;
  outline: none;
  border: none;
  flex: 1;
  min-height: 100%;

  ${({ centralize }) =>
    centralize &&
    css`
      text-align: center;
    `}

  line-height: ${getParagraphLineHeight['small']}px;
  font-size: ${getParagraphFontSize['small']}px;
  letter-spacing: ${getParagraphLetterSpacing('small')}px;
  font-weight: 400;
  color: ${({ theme }) => theme.neutral[0]};

  &::placeholder {
    color: ${({ theme }) => theme.neutral[400]};
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${({ theme }) => theme.neutral[600]};
    }
  }

  ${({ textAreaHeight }) =>
    textAreaHeight &&
    css`
      min-height: ${textAreaHeight};
      overflow-y: scroll;
      scrollbar-width: thin;
    `}
`;

export const CounterWrapper = styled.div<ICounterWrapperProps>`
  position: absolute;
  bottom: ${({ $size }) => `calc(${getTextInputPadding[$size]} - 3px)`};
  right: ${({ $size }) =>
    `calc(${getTextInputPadding[$size]} + 7px + ${SPACING._6})`};

  pointer-events: none;
`;
